/* QuizStage — shared UI atoms for the Career Clarity quizzes
   (B.Tech / BBA-MBA and After-10th group quizzes). UI only, no scoring. */
import React, { useEffect } from 'react';
import { Check, Star, Keyboard } from 'lucide-react';

const CHEERS = [
  'Take your time — there are no wrong answers',
  'Nice going! Pick what feels most like you',
  "You're doing great — keep it up",
  'Halfway there! Trust your first instinct',
  'Almost done — finish strong',
];
export const cheerFor = (idx, total) => {
  const p = (idx + 1) / total;
  if (idx === 0) return CHEERS[0];
  if (p < 0.35) return CHEERS[1];
  if (p < 0.55) return CHEERS[2];
  if (p < 0.8) return CHEERS[3];
  return CHEERS[4];
};

export const byId = (a, b) => String(a.id).localeCompare(String(b.id));

/* Answer list — letters always in order, keyboard A–E, staggered entrance. */
export const QuizOptions = ({ options, selectedId, onSelect, disabled, accent, renderText, testIdPrefix }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (disabled || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      const k = (e.key || '').toUpperCase();
      const opt = options.find((o) => String(o.id).toUpperCase() === k);
      if (opt) onSelect(opt);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [options, disabled, onSelect]);

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-options`}>
      {options.map((opt, i) => {
        const selected = opt.id === selectedId;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onSelect(opt)}
            disabled={disabled && !selected}
            style={{ animationDelay: `${i * 55}ms` }}
            className={`cc-opt group w-full text-left rounded-2xl border-2 px-4 py-3.5 md:px-5 md:py-4 flex items-center gap-4 transition-[transform,border-color,background-color,box-shadow] duration-200 active:scale-[0.99] ${
              selected
                ? `border-transparent bg-gradient-to-r ${accent.grad} text-white shadow-lg ${accent.shadow}`
                : `border-gray-200 bg-white ${accent.hover} hover:-translate-y-0.5 hover:shadow-md`
            }`}
            data-testid={`${testIdPrefix}-${opt.id}`}
          >
            <span
              className={`shrink-0 w-9 h-9 rounded-xl text-sm font-black flex items-center justify-center transition-colors ${
                selected ? 'bg-white/25 text-white' : `bg-gray-50 text-gray-700 border border-gray-200 shadow-[inset_0_-2px_0_rgba(0,0,0,0.08)] ${accent.keyHover}`
              }`}
            >
              {selected ? <Check size={16} strokeWidth={3} className="cc-tick-pop" /> : opt.id}
            </span>
            <span className={`text-sm md:text-base leading-relaxed ${selected ? 'font-semibold' : 'text-gray-800'}`}>
              {renderText ? renderText(opt) : opt.text}
            </span>
          </button>
        );
      })}
    </div>
  );
};

/* Footer hint under the options */
export const KeyHint = ({ last }) => (
  <p className="mt-5 text-xs text-gray-400 flex items-center justify-center gap-1.5 flex-wrap" data-testid="quiz-key-hint">
    <Keyboard size={13} /> Tap an answer or press
    <kbd className="px-1.5 py-0.5 rounded-md border border-gray-300 bg-gray-50 text-[10px] font-bold text-gray-600">A</kbd>–
    <kbd className="px-1.5 py-0.5 rounded-md border border-gray-300 bg-gray-50 text-[10px] font-bold text-gray-600">{last || 'E'}</kbd>
    <span>— we&apos;ll move you forward automatically</span>
  </p>
);

/* Left rail: gradient card with the big question counter + cheer line */
export const QuizSidePanel = ({ grad, eyebrow, title, index, total, testId }) => {
  const pct = Math.round(((index + 1) / total) * 100);
  return (
    <div className={`rounded-[1.5rem] bg-gradient-to-br ${grad} text-white p-5 shadow-lg cc-pop-in`} data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/80">{eyebrow}</p>
      <p className="text-sm font-extrabold mt-1 leading-snug">{title}</p>
      <div className="mt-4 flex items-end gap-1.5">
        <span className="text-5xl font-black leading-none tabular-nums" data-testid={`${testId}-counter`}>
          {String(index + 1).padStart(2, '0')}
        </span>
        <span className="text-white/70 font-bold mb-1">/ {total}</span>
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-white/25 overflow-hidden">
        <div className="h-full bg-white rounded-full transition-all duration-500 ease-out" style={{ width: `${pct}%` }} />
      </div>
      <p key={index} className="mt-3 text-xs text-white/90 flex items-start gap-1.5 cc-cheer-in">
        <Star size={12} className="fill-current mt-0.5 shrink-0" /> {cheerFor(index, total)}
      </p>
    </div>
  );
};

/* Small dot per question — answered / current / upcoming */
export const QuestionDots = ({ ids, answered, currentId, dotClass }) => (
  <div className="hidden md:flex flex-wrap gap-1.5 px-1" data-testid="quiz-question-dots">
    {ids.map((id) => {
      const isCur = id === currentId;
      const done = answered.has(id);
      return (
        <span
          key={id}
          className={`h-2 rounded-full transition-all duration-300 ${
            isCur ? `w-6 ${dotClass}` : done ? `w-2 ${dotClass} opacity-60` : 'w-2 bg-gray-200'
          }`}
        />
      );
    })}
  </div>
);
